import { useMemo } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DollarSign, TrendingUp, FileText, Clock, Ship } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

const EmployeeCommercialFinance = () => {
  // Real contracts from DB
  const { data: contracts = [], isLoading: loadingContracts } = useQuery({
    queryKey: ["contracts-all"],
    queryFn: async () => {
      const { data } = await supabase.from("contracts").select("*").order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  // Real vessel contracts — shows which vessels assigned to which clients
  const { data: vesselContracts = [] } = useQuery({
    queryKey: ["vessel-contracts-all"],
    queryFn: async () => {
      const { data } = await supabase.from("vessel_contracts").select("*, fleet_vessels(name, vessel_type, status), companies(name)").eq("is_active", true);
      return data ?? [];
    },
  });

  // Real fleet for day-rate context
  const { data: vessels = [] } = useQuery({
    queryKey: ["fleet-vessels"],
    queryFn: async () => {
      const { data } = await supabase.from("fleet_vessels").select("*").order("name");
      return data ?? [];
    },
  });

  const activeContracts = contracts.filter((c: any) => c.status === "active");

  // Build day-rate cards from real vessel contracts
  const dayRateCards = useMemo(() => {
    return vesselContracts.map((vc: any) => ({
      vessel: vc.fleet_vessels?.name || "Unknown",
      client: vc.companies?.name || "—",
      field: vc.field_name || "—",
      status: vc.fleet_vessels?.status || "unknown",
    }));
  }, [vesselContracts]);

  // Revenue placeholder chart (real data would come from invoicing module)
  const revenueData = useMemo(() => {
    const months = ["Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
    return months.map((m, i) => ({
      month: m,
      revenue: 380000 + Math.round(Math.random() * 200000),
      costs: 240000 + Math.round(Math.random() * 120000),
    }));
  }, []);

  return (
    <IntranetLayout>
      <div className="mb-6">
        <h1 className="font-display text-2xl text-foreground">Commercial & Finance</h1>
        <p className="text-sm text-muted-foreground font-body mt-1">Revenue tracking, contract management, and vessel assignments</p>
      </div>

      {/* KPIs from real data */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Active Contracts", value: activeContracts.length.toString(), icon: FileText, color: "text-teal" },
          { label: "Total Contracts", value: contracts.length.toString(), icon: DollarSign, color: "text-lime-dark" },
          { label: "Assigned Vessels", value: vesselContracts.length.toString(), icon: Ship, color: "text-blue-600" },
          { label: "Fleet Size", value: vessels.length.toString(), icon: TrendingUp, color: "text-emerald-600" },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <k.icon className={`w-8 h-8 ${k.color}`} />
              <div>
                <p className="text-xl font-display text-foreground">{k.value}</p>
                <p className="text-[11px] text-muted-foreground font-body">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="contracts">
        <TabsList>
          <TabsTrigger value="contracts">Contracts ({contracts.length})</TabsTrigger>
          <TabsTrigger value="assignments">Vessel Assignments ({dayRateCards.length})</TabsTrigger>
          <TabsTrigger value="revenue">Revenue Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="mt-4 space-y-2">
          {loadingContracts ? (
            <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
          ) : contracts.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No contracts found</p></CardContent></Card>
          ) : contracts.map((c: any) => (
            <Card key={c.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-display text-sm text-foreground">{c.client_name}</p>
                  <p className="text-xs text-muted-foreground font-body">
                    {c.contract_number || "—"} · {c.start_date ? new Date(c.start_date).toLocaleDateString() : "—"} – {c.end_date ? new Date(c.end_date).toLocaleDateString() : "Ongoing"}
                  </p>
                  {c.summary && <p className="text-xs text-muted-foreground/70 font-body mt-1 line-clamp-1">{c.summary}</p>}
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{c.status}</span>
                  {c.value && <p className="text-xs font-display text-foreground mt-1">{c.value}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="assignments" className="mt-4 space-y-2">
          {dayRateCards.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Ship className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No vessel assignments yet</p></CardContent></Card>
          ) : dayRateCards.map((dr: any, idx: number) => (
            <Card key={idx}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Ship className="w-6 h-6 text-teal" />
                  <div>
                    <p className="font-display text-sm text-foreground">{dr.vessel}</p>
                    <p className="text-xs text-muted-foreground font-body">{dr.client} · {dr.field}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${dr.status === "deployed" || dr.status === "available" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {dr.status}
                </span>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="revenue" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Revenue vs Costs (6 months)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v / 1000}K`} />
                  <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="revenue" fill="hsl(var(--teal))" radius={[4, 4, 0, 0]} name="Revenue" />
                  <Bar dataKey="costs" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} name="Costs" opacity={0.4} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default EmployeeCommercialFinance;
